import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { AlertTriangle, Sparkles, Users } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { breadcrumbSchema, faqPageSchema, localizedHead, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";

export const Route = createFileRoute("/promis")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const base = localizedHead("/promis", loaderData?.lang);
    const lang = loaderData?.lang ?? "en";
    const de = lang === "de";

    const faqs = de
      ? [
          { q: "Welche Promis nehmen Ozempic?", a: "Öffentlich über GLP-1-basierte Abnehmbehandlungen wie Ozempic, Wegovy oder Mounjaro gesprochen haben u.a. Oprah Winfrey, Elon Musk, Whoopi Goldberg, Amy Schumer, Chelsea Handler und Sharon Osbourne." },
          { q: "Nutzen Promis Retatrutide?", a: "Es gibt aktuell keine seriös bestätigten Promi-Anwender von Retatrutide. Der Wirkstoff (Reta Peptid, LY3437943) befindet sich noch in der klinischen Forschung (TRIUMPH-Phase-III) und ist nicht als frei verfügbares Arzneimittel zugelassen." },
          { q: "Warum ist Retatrutide bei Promis so ein Thema?", a: "Weil Retatrutide als Triple-Agonist auf GLP-1, GIP und Glucagon wirkt und in Studien einen deutlich höheren Gewichtsverlust als Semaglutid (Ozempic) zeigt, gilt es als die potenziell stärkere nächste Generation nach Ozempic und Mounjaro." },
          { q: "Wo kann man Retatrutide bekommen?", a: "Legaler Zugang ist derzeit nur über genehmigte klinische Studienprogramme oder eine ärztlich begleitete Behandlung in einer Partnerklinik möglich. Angebote unter Stichworten wie 'Retatrutide kaufen' sind mit erheblichen Risiken verbunden." },
        ]
      : [
          { q: "Which celebrities use Ozempic?", a: "Public figures who have openly discussed GLP-1-based weight-loss treatments like Ozempic, Wegovy or Mounjaro include Oprah Winfrey, Elon Musk, Whoopi Goldberg, Amy Schumer, Chelsea Handler and Sharon Osbourne." },
          { q: "Do celebrities use Retatrutide?", a: "There are currently no credibly confirmed celebrity users of Retatrutide. The compound (Reta peptide, LY3437943) is still in clinical research (TRIUMPH Phase III) and not yet approved as a freely available medicine." },
          { q: "Why is Retatrutide such a topic among celebrities?", a: "Because Retatrutide acts as a triple agonist on GLP-1, GIP and glucagon and shows markedly higher weight loss than Semaglutide (Ozempic) in trials, it is seen as the potentially stronger next generation after Ozempic and Mounjaro." },
          { q: "Where can I get Retatrutide?", a: "Legal access is currently only available through approved clinical study programmes or medically supervised treatment at a partner clinic. Offers under keywords like 'buy Retatrutide' carry significant risks." },
        ];

    const faqPage = faqPageSchema("/promis", loaderData?.lang, faqs);


    const article = {
      "@context": "https://schema.org",
      "@type": "Article",
      inLanguage: de ? "de-DE" : "en-US",
      headline: de
        ? "Retatrutide & Promis: Was Hollywood, Business und TV über GLP-1 sagen"
        : "Retatrutide & celebrities: what Hollywood, business and TV say about GLP-1",
      about: [
        { "@type": "Drug", name: "Retatrutide", alternateName: ["Reta", "LY3437943"] },
        { "@type": "Drug", name: "Semaglutide", alternateName: ["Ozempic", "Wegovy"] },
      ],
    };

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/promis", loaderData?.lang)) },
        { type: "application/ld+json", children: JSON.stringify(article) },
        { type: "application/ld+json", children: JSON.stringify(faqPage) },
      ],
    };
  },
  component: Page,
});

function Section({ eyebrow, title, children }: { eyebrow?: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-14">
      {eyebrow ? (
        <p className="text-[11px] font-semibold uppercase tracking-widest text-indigo">{eyebrow}</p>
      ) : null}
      <h2 className="mt-2 font-display text-2xl leading-tight text-foreground md:text-3xl">{title}</h2>
      <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

const CELEBS_DE = [
  { name: "Oprah Winfrey", note: "Sprach in einem TV-Special öffentlich über den Einsatz einer GLP-1-Abnehmspritze." },
  { name: "Elon Musk", note: "Nannte Wegovy/Ozempic mehrfach als Grund für seinen deutlichen Gewichtsverlust." },
  { name: "Whoopi Goldberg", note: "Berichtete in „The View\" offen über Mounjaro." },
  { name: "Amy Schumer", note: "Sprach in Interviews über frühere Erfahrungen mit Ozempic." },
  { name: "Chelsea Handler", note: "Erzählte in Podcasts, unwissentlich Ozempic verschrieben bekommen zu haben." },
  { name: "Sharon Osbourne", note: "Berichtete in Interviews über ihre Erfahrungen mit einer GLP-1-Therapie." },
];

const CELEBS_EN = [
  { name: "Oprah Winfrey", note: "Publicly discussed using a GLP-1 weight-loss medication in a TV special." },
  { name: "Elon Musk", note: "Repeatedly credited Wegovy/Ozempic for his notable weight loss." },
  { name: "Whoopi Goldberg", note: "Openly discussed Mounjaro on 'The View'." },
  { name: "Amy Schumer", note: "Spoke in interviews about earlier experiences with Ozempic." },
  { name: "Chelsea Handler", note: "Shared on podcasts that she was unknowingly prescribed Ozempic." },
  { name: "Sharon Osbourne", note: "Discussed her GLP-1 therapy experience in interviews." },
];

function Page() {
  const { lang } = useI18n();
  const de = lang === "de";
  const celebs = de ? CELEBS_DE : CELEBS_EN;

  const c = de
    ? {
        eyebrow: "Promis & GLP-1",
        h1a: "Retatrutide, Ozempic & ",
        h1em: "Promis",
        h1b: ": Warum Hollywood, TV und Business über diese Wirkstoffe reden",
        lead: "Von Oprah bis Elon Musk – viele Berühmtheiten haben offen über moderne GLP-1-Therapien gesprochen. Bei Retatrutide ist die Lage anders: der Wirkstoff steht noch in der klinischen Forschung.",
      }
    : {
        eyebrow: "Celebrities & GLP-1",
        h1a: "Retatrutide, Ozempic & ",
        h1em: "celebrities",
        h1b: ": why Hollywood, TV and business are talking about these compounds",
        lead: "From Oprah to Elon Musk, many public figures have openly discussed modern GLP-1 therapies. Retatrutide is different: the compound is still in clinical research.",
      };

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:py-20">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">{c.eyebrow}</p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {c.h1a}
          <em className="not-italic text-indigo">{c.h1em}</em>
          {c.h1b}
        </h1>
        <p className="mt-5 text-lg text-muted-foreground">{c.lead}</p>
      </motion.div>

      {de ? (
        <>
          <Section eyebrow="Hintergrund" title="Warum plötzlich alle über GLP-1-Abnehmspritzen sprechen">
            <p>
              Viele bekannte Persönlichkeiten aus Hollywood, TV und Business haben in den letzten Jahren offen über moderne GLP-1-basierte Abnehmbehandlungen wie <strong>Ozempic</strong>, <strong>Wegovy</strong> oder <strong>Mounjaro</strong> gesprochen. Namen wie <em>Oprah Winfrey, Elon Musk, Whoopi Goldberg</em> oder <em>Amy Schumer</em> zeigen, wie stark das Thema Gewichtsmanagement, Appetitkontrolle und Stoffwechseltherapie inzwischen in der Öffentlichkeit angekommen ist.
            </p>
            <p>
              Ozempic-Nutzung wird längst nicht mehr nur bei Diabetes-Patienten diskutiert – rote Teppiche, Talkshows und Business-Interviews haben den GLP-1-Effekt zum Mainstream gemacht.
            </p>
          </Section>

          <Section eyebrow="Bekannte Namen" title="Prominente, die öffentlich über Ozempic, Wegovy oder Mounjaro gesprochen haben">
            <p className="text-sm">
              Die folgende Übersicht basiert auf öffentlichen Äußerungen, Interviews und Podcasts. Sie ist ausdrücklich <strong>keine medizinische Empfehlung</strong> und keine Aussage darüber, welche Behandlung für eine einzelne Person geeignet ist.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {celebs.map((p) => (
                <div key={p.name} className="card-elevated p-5">
                  <div className="flex items-start gap-3">
                    <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-indigo/10 text-indigo">
                      <Sparkles className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="font-semibold text-foreground">{p.name}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{p.note}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section eyebrow="Reta bei Promis" title="Und Retatrutide? – Der wichtige Unterschied">
            <p>
              Bei <strong>Retatrutide</strong>, auch bekannt als <strong>Reta Peptid</strong> oder <strong>LY3437943</strong>, ist die Lage anders: Der Wirkstoff befindet sich noch in der klinischen Forschung und ist nicht als frei verfügbares Arzneimittel zugelassen. Deshalb gibt es aktuell <strong>keine seriös bestätigten Promi-Erfahrungen mit Retatrutide</strong>.
            </p>
            <p>
              Wer im Netz auf angebliche „Retatrutide Promi Erfahrungen", „Reta before/after Hollywood" oder ähnliche Schlagzeilen stößt, sollte skeptisch bleiben – das sind meist Klick-Überschriften ohne belastbare Quellen.
            </p>
            <p className="rounded-xl border border-warning/30 bg-warning/5 p-4 text-foreground">
              <AlertTriangle className="mr-2 inline h-4 w-4 text-warning" />
              Retatrutide ist kein Nahrungsergänzungsmittel und kein Lifestyle-Peptid. Es ist ein medizinischer Forschungswirkstoff, der in Phase-III-Studien untersucht wird.
            </p>
          </Section>

          <Section eyebrow="Warum es trotzdem alle diskutieren" title="Warum Reta bereits jetzt so intensiv besprochen wird">
            <p>
              Auch ohne Promi-Testimonials wird Retatrutide bereits intensiv diskutiert. Der Grund ist einfach: Reta wirkt als <strong>Triple-Agonist</strong> auf <strong>GLP-1</strong>, <strong>GIP</strong> und <strong>Glucagon</strong> – während Ozempic (Semaglutid) nur GLP-1 aktiviert.
            </p>
            <p>
              In den bisher veröffentlichten Studien zeigt Retatrutide einen deutlich höheren durchschnittlichen Gewichtsverlust als Ozempic und Mounjaro. Für Ärzte, Patienten und die interessierte Öffentlichkeit ist das der Hauptgrund, warum Reta als <strong>potenziell stärkere nächste Generation</strong> gilt.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {[
                { t: "Ozempic / Wegovy", b: "GLP-1 – bekannt durch Promi-Erfahrungen, ~15 % Gewichtsverlust." },
                { t: "Mounjaro / Zepbound", b: "GLP-1 + GIP (Dual) – stärker als Ozempic, ~20 % Gewichtsverlust." },
                { t: "Retatrutide (Reta)", b: "GLP-1 + GIP + Glucagon (Triple) – bis zu ~24 % in Studien." },
              ].map((x) => (
                <div key={x.t} className="card-elevated p-5">
                  <p className="font-mono text-sm font-semibold text-indigo">{x.t}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{x.b}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section eyebrow="Einordnung" title="Was das für die eigene Entscheidung bedeutet">
            <p>
              Prominente Erfahrungen mit Ozempic, Wegovy oder Mounjaro sind spannend – aber sie sind kein Ersatz für eine ärztliche Bewertung. Was bei Oprah, Elon Musk oder Whoopi Goldberg funktioniert, muss nicht automatisch der passende Weg für jeden Einzelnen sein.
            </p>
            <p>
              Wer sich für <strong>Retatrutide, Ozempic oder Mounjaro</strong> interessiert, sollte den seriösen Weg gehen: ärztliche Beratung, seriöse Klinik, klare Aufklärung über Wirkung, Nebenwirkungen und Zulassung – statt anonyme Online-Angebote unter Stichworten wie <em>„Retatrutide kaufen"</em> oder <em>„Reta Peptid ohne Rezept"</em>.
            </p>
          </Section>
        </>
      ) : (
        <>
          <Section eyebrow="Background" title="Why everyone suddenly talks about GLP-1 weight-loss drugs">
            <p>
              Many well-known figures from Hollywood, TV and business have openly discussed modern GLP-1-based weight-loss treatments like <strong>Ozempic</strong>, <strong>Wegovy</strong> and <strong>Mounjaro</strong>. Names like <em>Oprah Winfrey, Elon Musk, Whoopi Goldberg</em> and <em>Amy Schumer</em> show just how mainstream weight management, appetite control and metabolic therapy have become.
            </p>
            <p>
              Ozempic use is no longer discussed only in the diabetes community — red carpets, talk shows and business interviews have pushed the GLP-1 effect into the mainstream.
            </p>
          </Section>

          <Section eyebrow="Notable names" title="Celebrities who have publicly talked about Ozempic, Wegovy or Mounjaro">
            <p className="text-sm">
              The list below is based on public statements, interviews and podcasts. It is explicitly <strong>not medical advice</strong> and does not indicate which treatment is right for any specific person.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {celebs.map((p) => (
                <div key={p.name} className="card-elevated p-5">
                  <div className="flex items-start gap-3">
                    <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-indigo/10 text-indigo">
                      <Sparkles className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="font-semibold text-foreground">{p.name}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{p.note}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section eyebrow="Reta and celebrities" title="And Retatrutide? — the important difference">
            <p>
              With <strong>Retatrutide</strong> — also known as <strong>Reta peptide</strong> or <strong>LY3437943</strong> — the picture is different: the compound is still in clinical research and not yet approved as a freely available medicine. That's why there are currently <strong>no credibly confirmed celebrity experiences with Retatrutide</strong>.
            </p>
            <p>
              Any headlines claiming "Retatrutide celebrity results" or "Reta before/after Hollywood" should be treated with skepticism — most are clickbait without reliable sources.
            </p>
            <p className="rounded-xl border border-warning/30 bg-warning/5 p-4 text-foreground">
              <AlertTriangle className="mr-2 inline h-4 w-4 text-warning" />
              Retatrutide is not a supplement and not a lifestyle peptide. It is an investigational medical compound being studied in Phase III trials.
            </p>
          </Section>

          <Section eyebrow="Why it's already a hot topic" title="Why Reta is being discussed so intensely already">
            <p>
              Even without celebrity testimonials, Retatrutide is already being discussed intensely. The reason is simple: Reta acts as a <strong>triple agonist</strong> on <strong>GLP-1</strong>, <strong>GIP</strong> and <strong>glucagon</strong> — while Ozempic (Semaglutide) only activates GLP-1.
            </p>
            <p>
              In published trials, Retatrutide shows markedly higher average weight loss than Ozempic or Mounjaro. That is the main reason Reta is seen as the <strong>potentially stronger next generation</strong>.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {[
                { t: "Ozempic / Wegovy", b: "GLP-1 — famous via celebrity stories, ~15% weight loss." },
                { t: "Mounjaro / Zepbound", b: "GLP-1 + GIP (dual) — stronger than Ozempic, ~20% weight loss." },
                { t: "Retatrutide (Reta)", b: "GLP-1 + GIP + glucagon (triple) — up to ~24% in trials." },
              ].map((x) => (
                <div key={x.t} className="card-elevated p-5">
                  <p className="font-mono text-sm font-semibold text-indigo">{x.t}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{x.b}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section eyebrow="Framing" title="What this means for your own decision">
            <p>
              Celebrity experiences with Ozempic, Wegovy or Mounjaro are interesting — but they are not a substitute for a proper medical assessment. What works for Oprah, Elon Musk or Whoopi Goldberg is not automatically the right path for any given individual.
            </p>
            <p>
              Anyone interested in <strong>Retatrutide, Ozempic or Mounjaro</strong> should take the reputable route: medical consultation, a serious clinic, and clear information about effect, side effects and approval status — rather than anonymous online offers under keywords like <em>"buy Retatrutide"</em> or <em>"Reta peptide no prescription"</em>.
            </p>
          </Section>
        </>
      )}

      <div className="mt-12 rounded-2xl border border-indigo/30 bg-gradient-to-br from-indigo/10 via-background to-emerald/10 p-6 md:p-8">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-indigo text-indigo-foreground">
            <Users className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-2xl text-foreground">
              {de ? "Persönliche Beratung zu Retatrutide" : "Personal consultation on Retatrutide"}
            </h3>
            <p className="mt-2 text-muted-foreground">
              {de
                ? "Kostenloses Erstgespräch mit einem spezialisierten Ärzteteam – auf Deutsch oder Englisch."
                : "Free initial consultation with a specialised medical team – in English or German."}
            </p>
            <div className="mt-4">
              <ConsultCta />
            </div>
          </div>
        </div>
      </div>

      <RelatedLinks only={["/vergleich", "/wissenschaft", "/studien"]} />
    </div>
  );
}
